import { useMemo, useState } from 'react';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  useAddMemberCoachesMutation,
  useRemoveMemberCoachesMutation,
} from '@backrs_ui/grow-ui/src/redux/coachingDashboard/coachingDashboard.api';
import type { CoachSummary } from '@backrs_ui/grow-ui/src/redux/coachingDashboard/coachingDashboard.types';

import { CoachAssignmentModalView } from './CoachAssignmentModalView';

const {
  modules: { coachingDashboard },
} = appStrings;

type EditMemberCoachesModalProps = Readonly<{
  open: boolean;
  onClose: () => void;
  memberId: number;
  memberName: string;
  currentCoaches: CoachSummary[];
  onSuccess?: () => void;
}>;

export const EditMemberCoachesModal = ({
  open,
  onClose,
  memberId,
  memberName,
  currentCoaches,
  onSuccess,
}: EditMemberCoachesModalProps) => {
  const [addMemberCoaches, { isLoading: isAdding }] = useAddMemberCoachesMutation();
  const [removeMemberCoaches, { isLoading: isRemoving }] = useRemoveMemberCoachesMutation();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const lockedCoachIds = useMemo(
    () => new Set(currentCoaches.filter((c) => c.is_institution_member !== true).map((c) => c.id)),
    [currentCoaches],
  );

  const isSaving = isAdding || isRemoving;

  const handleSave = async (selected: CoachSummary[]) => {
    const currentIds = new Set(currentCoaches.map((c) => c.id));
    const selectedIds = new Set(selected.map((c) => c.id));
    const toAdd = selected.filter((c) => !currentIds.has(c.id)).map((c) => c.id);
    const toRemove = currentCoaches
      .filter((c) => !selectedIds.has(c.id) && !lockedCoachIds.has(c.id))
      .map((c) => c.id);

    if (toAdd.length === 0 && toRemove.length === 0) {
      onClose();

      return;
    }

    const [addResult, removeResult] = await Promise.allSettled([
      toAdd.length > 0
        ? addMemberCoaches({ userId: memberId, coachIds: toAdd }).unwrap()
        : Promise.resolve(null),
      toRemove.length > 0
        ? removeMemberCoaches({ userId: memberId, coachIds: toRemove }).unwrap()
        : Promise.resolve(null),
    ]);

    const addFailed = toAdd.length > 0 && addResult.status === 'rejected';
    const removeFailed = toRemove.length > 0 && removeResult.status === 'rejected';
    const anySucceeded =
      (toAdd.length > 0 && addResult.status === 'fulfilled') ||
      (toRemove.length > 0 && removeResult.status === 'fulfilled');

    if (addFailed) {
      console.error('Failed to add member coaches:', (addResult as PromiseRejectedResult).reason);
    }
    if (removeFailed) {
      console.error(
        'Failed to remove member coaches:',
        (removeResult as PromiseRejectedResult).reason,
      );
    }

    if (anySucceeded) onSuccess?.();

    if (!addFailed && !removeFailed) {
      onClose();

      return;
    }

    if (addFailed && removeFailed) {
      setErrorMessage(coachingDashboard.bulkAssignError);
    } else if (addFailed) {
      setErrorMessage(coachingDashboard.editCoachesAddFailed);
    } else {
      setErrorMessage(coachingDashboard.editCoachesRemoveFailed);
    }
  };

  return (
    <CoachAssignmentModalView
      open={open}
      onClose={onClose}
      description={coachingDashboard.coachAssignment(memberName)}
      initialSelectedCoaches={currentCoaches}
      lockedCoachIds={lockedCoachIds}
      isSaving={isSaving}
      errorMessage={errorMessage}
      onCloseError={() => setErrorMessage(null)}
      onSave={handleSave}
    />
  );
};
